import { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import statesTopo from "us-atlas/states-10m.json";
import { MapPin, Sparkles, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COLOR = { covered: "#0f2740", open: "#c8102e" };

function Pin({ p, hovered, setHovered }) {
  const isH = hovered === p.zip;
  const label = `${p.zip} ${p.city}, ${p.state} · ${p.reps.length ? p.reps.map((r) => r.code).join(", ") : "No rep"}`;
  const w = label.length * 6.2 + 16;
  return (
    <Marker coordinates={[p.lng, p.lat]} onMouseEnter={() => setHovered(p.zip)} onMouseLeave={() => setHovered(null)}>
      <circle r={isH ? 8 : 5.5} fill={COLOR[p.status]} stroke="#fff" strokeWidth={1.5} data-testid={`territory-pin-${p.zip}`} />
      {isH && (
        <g transform="translate(0,-14)" style={{ pointerEvents: "none" }}>
          <rect x={-w / 2} y={-19} width={w} height={20} rx={5} fill="#0f2740" opacity={0.96} />
          <text x={0} y={-5} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={600}>{label}</text>
        </g>
      )}
    </Marker>
  );
}

const FIELDS = [["sms", "Text message"], ["email_subject", "Email subject"], ["email_body", "Email"], ["social", "Social post"]];

function RecruitDialog({ zip, onClose }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/ai/recruit-message", { zip, notes }); setMsg(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI writer unavailable"); }
    setBusy(false);
  };
  useEffect(() => { if (zip) { setMsg(null); gen(); } }, [zip]); // eslint-disable-line react-hooks/exhaustive-deps
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  return (
    <Dialog open={!!zip} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-auto" data-testid="recruit-dialog">
        <DialogHeader><DialogTitle>Recruiting messages for {zip}{msg?.city ? ` · ${msg.city}, ${msg.state}` : ""}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes, e.g. mention the county fair" className="h-8 text-xs" data-testid="recruit-notes" />
          <Button size="sm" className="h-8 bg-sam-navy" disabled={busy} onClick={gen} data-testid="recruit-regenerate">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}{msg ? "Rewrite" : "Write"}</Button>
        </div>
        {busy && !msg && <p className="text-xs text-sam-slate" data-testid="recruit-loading">Claude is writing…</p>}
        {msg && FIELDS.map(([k, label]) => (
          <div key={k} className="rounded-lg border border-border p-2.5" data-testid={`recruit-${k}`}>
            <div className="flex items-center justify-between mb-1"><span className="text-[11px] font-semibold text-sam-navy">{label}</span>
              <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => copy(msg[k])} data-testid={`recruit-copy-${k}`}><Copy className="h-3 w-3 mr-1" />Copy</Button></div>
            <p className="text-xs whitespace-pre-wrap text-sam-slate">{msg[k]}</p>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function ZipRow({ p, onRecruit }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/50 py-1.5 text-xs" data-testid={`territory-row-${p.zip}`}>
      <span className="font-mono font-semibold text-sam-navy">{p.zip}</span>
      <span className="flex-1 text-sam-slate truncate">{p.city ? `${p.city}, ${p.state}` : "Unknown ZIP"}{p.runs.length ? ` · ${p.runs.map((r) => r.route_code).join(", ")}` : ""}</span>
      {p.reps.length
        ? <span className="text-sam-navy">{p.reps.map((r, i) => `${i === 0 ? "★ " : ""}${r.name} (${r.code})`).join(" · ")}</span>
        : <span className="font-semibold text-sam-crimson">Open — recruit here</span>}
      <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onRecruit(p.zip)} data-testid={`territory-recruit-${p.zip}`}><Sparkles className="h-3 w-3 mr-1" />Recruit msg</Button>
    </div>
  );
}

export default function TerritoryMap() {
  const [d, setD] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [recruitZip, setRecruitZip] = useState(null);
  const [anyZip, setAnyZip] = useState("");
  useEffect(() => { api.get("/admin/territory-map").then(({ data }) => setD(data)).catch(() => setD({ points: [], reps: [] })); }, []);
  if (!d) return null;
  const open = d.points.filter((p) => p.status === "open").length;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="territory-map">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="font-display font-bold text-sam-navy inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-sam-crimson" />ZIP coverage map</h3>
        <div className="flex gap-3 text-xs" data-testid="territory-legend">
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLOR.covered }} />Rep covers ZIP</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLOR.open }} />Run ZIP with no rep</span>
        </div>
      </div>
      <p className="text-xs text-sam-slate mb-3">{d.reps.length} rep{d.reps.length === 1 ? "" : "s"} with area ZIPs · <span className="font-semibold text-sam-crimson" data-testid="territory-open-count">{open} open ZIP{open === 1 ? "" : "s"}</span> to recruit for. ★ = first in line for new runs.</p>
      <div className="rounded-xl border border-border bg-white overflow-hidden">
        <ComposableMap projection="geoAlbersUsa" width={900} height={500} style={{ width: "100%", height: "auto" }}>
          <Geographies geography={statesTopo}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography key={geo.rsmKey} geography={geo} fill="#eef1f4" stroke="#cdd5df" strokeWidth={0.5} style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }} />
            ))}
          </Geographies>
          {d.points.filter((p) => p.lat != null).map((p) => <Pin key={p.zip} p={p} hovered={hovered} setHovered={setHovered} />)}
        </ComposableMap>
      </div>
      <div className="mt-3 max-h-64 overflow-auto" data-testid="territory-list">
        {d.points.length === 0 ? <p className="text-sm text-sam-slate">No area ZIPs or run ZIPs yet — add area ZIPs to team members below.</p> : d.points.map((p) => <ZipRow key={p.zip} p={p} onRecruit={setRecruitZip} />)}
      </div>
      <div className="mt-3 flex items-center gap-2" data-testid="territory-any-zip">
        <Input value={anyZip} onChange={(e) => setAnyZip(e.target.value)} placeholder="Any ZIP, e.g. 65616" className="h-8 w-40 text-xs" data-testid="territory-any-zip-input" />
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!/^\d{5}$/.test(anyZip.trim())} onClick={() => setRecruitZip(anyZip.trim())} data-testid="territory-any-zip-btn"><Sparkles className="h-3 w-3 mr-1" />Write recruiting messages</Button>
      </div>
      <RecruitDialog zip={recruitZip} onClose={() => setRecruitZip(null)} />
    </div>
  );
}
